import { assetUrl } from "@/lib/api";
import { Briefcase, FileText, GraduationCap, MapPin, Send } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CompanyLogo } from "@/components/company/CompanyLogo";
import { useI18n } from "@/lib/i18n/i18n";
import { appCompany } from "@/lib/demo/companies";

const JobCard = ({ job: [title, type, area, tits, loc], idx, t }) => (
  <li className="rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-upv" data-testid={`company-job-${idx}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <p className="font-display text-lg font-bold">{t(title)}</p>
      <span className="rounded-full bg-upv-light px-3 py-1 text-[11px] font-bold text-upv">{t(type)}</span>
    </div>
    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
      {area && <span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5" /> {t(area)}</span>}
      <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {loc || "València"}</span>
      {tits?.length > 0 && <span className="flex items-center gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> {tits.map((x) => t(x)).join(" · ")}</span>}
    </div>
  </li>
);

export const ProfileView = ({ company, aside, topLeft }) => {
  const { t, L } = useI18n();
  return (
    <>
      <div className="relative h-64 overflow-hidden sm:h-80">
        <img src={assetUrl(company.image)} alt={company.name} className="h-full w-full object-cover" data-testid="company-profile-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent" />
        {topLeft}
      </div>
      <main className="mx-auto max-w-6xl px-5 lg:px-10">
        <div className="-mt-14 flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="relative flex h-28 w-full max-w-[260px] items-center justify-center rounded-3xl border border-slate-200 bg-white px-6 shadow-lg"><CompanyLogo id={company.id} logo={company.logo} size="text-3xl" className="text-center" /></div>
          <div className="sm:pt-16">
            <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="company-profile-name">{company.name}</h1>
            <p className="mt-1 text-sm text-slate-500">{t(company.sector)}{company.stand ? ` · Stand ${company.stand}` : ""} · {t("Foro de Empleo UPV 2027")}</p>
          </div>
        </div>
        <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-10">
            <section>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Sobre la empresa")}</h2>
              <p className="mt-3 whitespace-pre-line text-lg leading-relaxed text-slate-700" data-testid="company-profile-description">{typeof company.desc === "string" ? company.desc : L(company.desc)}</p>
            </section>
            <section>
              <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Oportunidades de empleo")}</h2>
              <ul className="mt-4 space-y-3">{company.jobs.map((j, i) => <JobCard key={j[0] + i} job={j} idx={i} t={t} />)}</ul>
            </section>
          </div>
          {aside}
        </div>
      </main>
    </>
  );
};

const PreviewAside = () => {
  const { t } = useI18n();
  return (
    <div className="h-fit rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-upv">{t("Comparte tu currículum")}</p>
      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-slate-50 p-4"><FileText className="h-5 w-5 text-upv" /><span className="h-3 w-32 rounded-full bg-slate-200" /></div>
      <div className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-upv font-display font-bold tracking-wider text-white opacity-60"><Send className="h-4 w-4" /> {t("ENVIAR MI CURRÍCULUM")}</div>
    </div>
  );
};

export const ProfilePreview = ({ open, onOpenChange, data }) => {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl gap-0 overflow-y-auto overflow-x-hidden bg-slate-50 p-0 pb-10" data-testid="company-preview-dialog">
        <DialogTitle className="sr-only">{t("Vista previa del perfil")}</DialogTitle>
        {open && (
          <ProfileView
            company={appCompany(data)}
            aside={<PreviewAside />}
            topLeft={<span className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-upv backdrop-blur" data-testid="company-preview-badge">{t("Vista previa")}</span>}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
